import { useEffect, useMemo, useState } from "react";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import { useParams } from "react-router";
import { InitiativeCard } from "./initiative-card";
import { InitiativeCardSkeleton } from "./initiative-card-skeleton";
import { InitiativesFilters } from "./initiatives-filters";
import { normalizeAmbassadorInitiatives } from "./types";
import {
  filterInitiatives,
  getHasActiveInitiativesFilters,
  INITIAL_INITIATIVES_FILTER_VALUES,
  type InitiativesFilterValues,
} from "./utils";

const SKELETON_COUNT = 6;

function AmbassadorInitiatives({
  getAmbassadorInitiativesList,
  ambassadorInitiativesList,
  ambassadorDetails,
}: any) {
  const { id: routeAmbassadorId }: any = useParams();
  const ambassadorId = routeAmbassadorId || ambassadorDetails?.id;
  const [filters, setFilters] = useState<InitiativesFilterValues>(
    INITIAL_INITIATIVES_FILTER_VALUES
  );

  useEffect(() => {
    if (!ambassadorId) return;
    getAmbassadorInitiativesList({ ambassadorId });
  }, [ambassadorId]);

  const loading = Boolean(ambassadorInitiativesList?.loading);
  const isError = ambassadorInitiativesList?.error;
  const hasLoaded = Array.isArray(ambassadorInitiativesList?.data);

  const allInitiatives = useMemo(
    () => normalizeAmbassadorInitiatives(ambassadorInitiativesList?.data),
    [ambassadorInitiativesList?.data]
  );

  const filteredInitiatives = useMemo(
    () => filterInitiatives(allInitiatives, filters),
    [allInitiatives, filters]
  );

  const hasActiveFilters = getHasActiveInitiativesFilters(filters);
  const isFilteredEmpty = allInitiatives.length > 0 && hasActiveFilters;

  const handleRetry = () => {
    if (!ambassadorId) return;
    getAmbassadorInitiativesList({ ambassadorId });
  };

  const renderContent = () => {
    if (loading || (!hasLoaded && !isError)) {
      return (
        <Grid container spacing={2}>
          {Array.from({ length: SKELETON_COUNT }, (_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <InitiativeCardSkeleton />
            </Grid>
          ))}
        </Grid>
      );
    }

    if (isError) {
      return (
        <Box
          py={3}
          textAlign="center"
          border="1px dashed #FCA5A5"
          borderRadius={2}
          bgcolor="#FEF2F2"
        >
          <Typography fontSize={14} color="#B91C1C" mb={1.5}>
            Something went wrong while loading initiatives. Please try again.
          </Typography>
          <Button size="small" variant="outlined" onClick={handleRetry}>
            Retry
          </Button>
        </Box>
      );
    }

    if (filteredInitiatives.length === 0) {
      return (
        <Box py={5} px={3} textAlign="center">
          <AssignmentOutlinedIcon
            sx={{ fontSize: 40, color: "#D1D5DB", mb: 1.5 }}
          />
          <Typography fontSize={15} fontWeight={600} color="#374151">
            {isFilteredEmpty
              ? "No initiatives match your filters"
              : "No initiatives found"}
          </Typography>
          <Typography
            fontSize={13}
            color="#6B7280"
            mt={0.5}
            maxWidth={360}
            mx="auto"
          >
            {isFilteredEmpty
              ? "Try a different title, type, or visibility filter."
              : "This ambassador has not added any initiatives yet."}
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        {filteredInitiatives.map((initiative) => (
          <Grid item xs={12} sm={6} md={4} key={initiative.id}>
            <InitiativeCard initiative={initiative} />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Stack gap={3}>
      <InitiativesFilters onChange={setFilters} disabled={loading} />
      {renderContent()}
    </Stack>
  );
}

export { AmbassadorInitiatives };
